import React from 'react';
import type { CubeMemberInfo } from 'shared/src/trpc-types/cube-load-trpc-types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/forms/ui/tooltip';

type MemberBadgeProps = {
  memberRef: string;
  info?: CubeMemberInfo;
};

const colorByType = {
  measure:
    'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-800',
  dimension:
    'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:ring-violet-800'
} as const;

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="shrink-0 font-medium text-muted-foreground">{label}</span>
      <span className="break-all text-foreground">{value}</span>
    </div>
  );
}

function TagList({ items }: { items: string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(item => (
        <span
          key={item}
          className="inline-flex rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
          {item}
        </span>
      ))}
    </div>
  );
}

function MemberTooltipContent({ info }: { info: CubeMemberInfo }) {
  const isMeasure = info.memberType === 'measure';
  const { meta } = info;

  return (
    <div className="max-w-md space-y-2.5 text-left text-xs">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{info.title}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
              isMeasure
                ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                : 'bg-violet-500/15 text-violet-600 dark:text-violet-400'
            }`}>
            {info.memberType}
          </span>
        </div>
        {info.description ? (
          <div className="mt-1 text-muted-foreground">{info.description}</div>
        ) : null}
      </div>

      {/* Identity */}
      <div className="space-y-0.5 text-[11px]">
        <MetaRow label="cube:" value={<code className="text-[10px]">{info.cubeName}</code>} />
        <MetaRow label="ref:" value={<code className="text-[10px]">{info.memberRef}</code>} />
      </div>

      {/* Classification tags */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {meta.category ? <MetaRow label="category:" value={meta.category} /> : null}
        {meta.type ? <MetaRow label="type:" value={meta.type} /> : null}
        {'grain' in meta && meta.grain ? <MetaRow label="grain:" value={meta.grain} /> : null}
        {'period' in meta && meta.period ? <MetaRow label="period:" value={meta.period} /> : null}
        <MetaRow label="data:" value={info.type} />
        {isMeasure && 'aggType' in info ? <MetaRow label="agg:" value={info.aggType} /> : null}
        {'format' in info && info.format ? <MetaRow label="format:" value={info.format} /> : null}
        {'primaryKey' in info && info.primaryKey ? (
          <MetaRow label="key:" value="primary key" />
        ) : null}
      </div>

      {/* Flags */}
      {isMeasure && 'supports_rollup' in meta ? (
        <div className="flex gap-2 text-[11px]">
          <span className={meta.supports_rollup ? 'text-green-600' : 'text-amber-600'}>
            {meta.supports_rollup ? 'rollup' : 'no rollup'}
          </span>
          {'supports_timeseries' in meta && meta.supports_timeseries !== undefined ? (
            <span className={meta.supports_timeseries ? 'text-green-600' : 'text-amber-600'}>
              {meta.supports_timeseries ? 'timeseries' : 'no timeseries'}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Formula / SQL */}
      {meta.formula ? (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">Formula</div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border bg-muted px-2.5 py-2 text-[11px] leading-relaxed text-foreground">
            {meta.formula}
          </pre>
        </div>
      ) : null}

      {info.sql ? (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">SQL</div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border bg-muted px-2.5 py-2 text-[11px] leading-relaxed text-foreground">
            {info.sql}
          </pre>
        </div>
      ) : null}

      {/* Explanation */}
      {meta.explanation ? (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">Explanation</div>
          <div className="text-foreground">{meta.explanation}</div>
        </div>
      ) : null}

      {/* Inputs */}
      {meta.inputs?.length ? (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">Inputs</div>
          <TagList items={meta.inputs} />
        </div>
      ) : null}

      {/* Required dimensions */}
      {'required_dimensions' in meta && meta.required_dimensions?.length ? (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">
            Required dimensions
          </div>
          <TagList items={meta.required_dimensions} />
        </div>
      ) : null}

      {/* Lineage */}
      {meta.rolled_up_from ? (
        <MetaRow
          label="rolled up from:"
          value={<code className="text-[10px]">{meta.rolled_up_from}</code>}
        />
      ) : null}

      {/* Source */}
      {meta.source_tables?.length ? (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase text-muted-foreground">
            Source tables
          </div>
          <TagList items={meta.source_tables} />
        </div>
      ) : null}
    </div>
  );
}

export function MemberBadge({ memberRef, info }: MemberBadgeProps) {
  const label = info?.title ?? memberRef;
  const color = info?.memberType
    ? colorByType[info.memberType]
    : 'bg-muted text-foreground ring-border';

  const badge = (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${color}`}>
      {label}
    </span>
  );

  if (!info) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="border border-border bg-popover p-3 text-popover-foreground shadow-lg">
        <MemberTooltipContent info={info} />
      </TooltipContent>
    </Tooltip>
  );
}
